/**
 * MDM Audit Console — Story 11.6 (Decision Provenance)
 *
 * Value-free, tamper-evident record of all merge/no-match/escalate decisions.
 * Displays aggregates, breakdown chart, and paginated decision table with
 * SHA-256 audit hash chain verification.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  Shield,
  Download,
  RefreshCw,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  X,
} from "lucide-react";
import {
  getAuditDecisions,
  verifyAuditChain,
  auditDecisionsCsvUrl,
} from "../api/mdm-client";
import type {
  AuditDecisionRow,
  AuditAggregates,
  AuditDecisionFilters,
} from "../types";

// ─── Sub-components ──────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-[var(--border-muted)] bg-white/[0.04] px-4 py-3 space-y-1">
      <p className="text-xs text-white/50">{label}</p>
      <p className="text-lg font-semibold text-white/90">{value}</p>
      {sub && <p className="text-xs text-white/40">{sub}</p>}
    </div>
  );
}

function Badge({
  label,
  variant,
}: {
  label: string;
  variant: "green" | "red" | "yellow" | "blue" | "gray";
}) {
  const cls = {
    green: "bg-emerald-500/15 text-emerald-300 border-emerald-500/20",
    red: "bg-red-500/15 text-red-300 border-red-500/20",
    yellow: "bg-yellow-500/15 text-yellow-300 border-yellow-500/20",
    blue: "bg-blue-500/15 text-blue-300 border-blue-500/20",
    gray: "bg-white/[0.06] text-white/60 border-white/[0.08]",
  }[variant];
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium border ${cls}`}
    >
      {label}
    </span>
  );
}

function HashChip({ hash }: { hash: string }) {
  const [copied, setCopied] = useState(false);
  const short = hash.slice(0, 12) + "…";
  const copy = () => {
    navigator.clipboard.writeText(hash).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={copy}
      title={hash}
      className="flex items-center gap-1 font-mono text-[10px] text-white/50 hover:text-white/80 transition"
    >
      <span>{short}</span>
      {copied ? (
        <Check className="w-3 h-3 text-emerald-400" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </button>
  );
}

function VerifyModal({
  runId,
  onClose,
}: {
  runId: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<{
    verified: boolean;
    broken_at: string | null;
    total: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    verifyAuditChain(runId)
      .then(setResult)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [runId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6">
      <div className="w-full max-w-sm bg-[var(--surface-dark)] rounded-2xl border border-[var(--border-muted)] p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-white/90">
            Hash Chain Verification
          </h3>
          <button onClick={onClose}>
            <X className="w-4 h-4 text-white/40 hover:text-white/70" />
          </button>
        </div>
        {loading && (
          <div className="flex items-center gap-3 text-sm text-white/60">
            <RefreshCw className="w-4 h-4 animate-spin" /> Verifying…
          </div>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
        {result && (
          <div className="space-y-3">
            {result.verified ? (
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">
                  Chain intact — {result.total} decisions verified
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-red-300">
                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Tamper detected</p>
                  {result.broken_at && (
                    <p className="text-xs text-red-400 mt-0.5">
                      First broken at:{" "}
                      <span className="font-mono">
                        {result.broken_at.slice(0, 16)}…
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Decision row badge helpers ───────────────────────────────────────────────

function decisionVariant(d: string): "green" | "red" | "yellow" | "gray" {
  if (d === "match") return "green";
  if (d === "no_match") return "red";
  if (d === "escalate") return "yellow";
  return "gray";
}

function sourceVariant(s: string): "blue" | "green" | "yellow" | "gray" {
  if (s === "steward") return "blue";
  if (s === "auto") return "green";
  if (s === "llm") return "yellow";
  return "gray";
}

// ─── Main view ────────────────────────────────────────────────────────────────

export interface MdmAuditConsoleViewProps {
  runId?: string;
  projectId: string;
}

const PAGE_SIZE = 50;

export default function MdmAuditConsoleView({
  runId: initialRunId,
  projectId: _projectId,
}: MdmAuditConsoleViewProps) {
  // Filters
  const [filterRunId, setFilterRunId] = useState(initialRunId ?? "");
  const [filterDecision, setFilterDecision] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterZone, setFilterZone] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");

  // Data
  const [decisions, setDecisions] = useState<AuditDecisionRow[]>([]);
  const [aggregates, setAggregates] = useState<AuditAggregates | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showVerify, setShowVerify] = useState(false);

  const buildFilters = useCallback(
    (): AuditDecisionFilters => ({
      run_id: filterRunId || undefined,
      decision: filterDecision || undefined,
      decision_source: filterSource || undefined,
      zone: filterZone || undefined,
      decided_from: filterFrom || undefined,
      decided_to: filterTo || undefined,
      limit: PAGE_SIZE,
      offset,
    }),
    [
      filterRunId,
      filterDecision,
      filterSource,
      filterZone,
      filterFrom,
      filterTo,
      offset,
    ],
  );

  const fetch = useCallback(
    async (off: number) => {
      setLoading(true);
      setError(null);
      try {
        const filters = buildFilters();
        filters.offset = off;
        const result = await getAuditDecisions(filters);
        setDecisions(result.decisions ?? []);
        setAggregates(result.aggregates ?? null);
        setTotal(result.total ?? 0);
        setOffset(off);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load audit data.");
      } finally {
        setLoading(false);
      }
    },
    [buildFilters],
  );

  useEffect(() => {
    fetch(0);
  }, [
    filterRunId,
    filterDecision,
    filterSource,
    filterZone,
    filterFrom,
    filterTo,
  ]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const csvUrl = auditDecisionsCsvUrl(buildFilters());

  const agg = aggregates;

  return (
    <div className="flex flex-col gap-5 p-6">
      {showVerify && filterRunId && (
        <VerifyModal runId={filterRunId} onClose={() => setShowVerify(false)} />
      )}

      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-semibold text-white/90">Audit Console</h2>
          <span className="text-xs text-white/40">
            Decision provenance · value-free
          </span>
        </div>
        <div className="flex items-center gap-2">
          {filterRunId && (
            <button
              onClick={() => setShowVerify(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-violet-500/30 bg-violet-500/10 text-violet-300 hover:bg-violet-500/15 transition"
            >
              <Shield className="w-3.5 h-3.5" />
              Verify Hash Chain
            </button>
          )}
          <a
            href={csvUrl}
            download="mdm-audit-decisions.csv"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-[var(--border-muted)] bg-white/[0.05] text-white/60 hover:bg-white/[0.08] transition"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </a>
        </div>
      </div>

      {/* Filter bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        <input
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 placeholder-white/25 focus:outline-none focus:border-violet-500/40"
          placeholder="Run ID…"
          value={filterRunId}
          onChange={(e) => setFilterRunId(e.target.value)}
        />
        <select
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 focus:outline-none"
          value={filterDecision}
          onChange={(e) => setFilterDecision(e.target.value)}
        >
          <option value="">All decisions</option>
          <option value="match">Match</option>
          <option value="no_match">No match</option>
          <option value="escalate">Escalate</option>
        </select>
        <select
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 focus:outline-none"
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
        >
          <option value="">All sources</option>
          <option value="auto">Auto</option>
          <option value="llm">LLM</option>
          <option value="steward">Steward</option>
        </select>
        <input
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 placeholder-white/25 focus:outline-none focus:border-violet-500/40"
          placeholder="Zone…"
          value={filterZone}
          onChange={(e) => setFilterZone(e.target.value)}
        />
        <input
          type="date"
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 focus:outline-none"
          value={filterFrom}
          onChange={(e) => setFilterFrom(e.target.value)}
        />
        <input
          type="date"
          className="rounded-lg px-3 py-1.5 text-xs bg-white/[0.06] border border-[var(--border-muted)] text-white/80 focus:outline-none"
          value={filterTo}
          onChange={(e) => setFilterTo(e.target.value)}
        />
      </div>

      {/* Aggregate tiles */}
      {agg && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatCard label="Total Decided" value={agg.total.toLocaleString()} />
          <StatCard
            label="Auto %"
            value={`${agg.auto_pct?.toFixed(1) ?? 0}%`}
            sub="Rule-based auto-decisions"
          />
          <StatCard
            label="LLM %"
            value={`${agg.llm_pct?.toFixed(1) ?? 0}%`}
            sub="AI adjudicated"
          />
          <StatCard
            label="Steward %"
            value={`${agg.steward_pct?.toFixed(1) ?? 0}%`}
            sub="Manual review"
          />
        </div>
      )}

      {/* Decision breakdown progress bar */}
      {agg && agg.total > 0 && (
        <div className="space-y-1">
          <div className="flex gap-0 rounded-full overflow-hidden h-2">
            <div
              className="bg-emerald-500 transition-all"
              style={{ width: `${(agg.merge_count / agg.total) * 100}%` }}
            />
            <div
              className="bg-red-500 transition-all"
              style={{ width: `${(agg.no_match_count / agg.total) * 100}%` }}
            />
            <div
              className="bg-yellow-500 transition-all"
              style={{ width: `${(agg.escalate_count / agg.total) * 100}%` }}
            />
          </div>
          <div className="flex gap-4 text-[10px] text-white/50">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
              Match ({agg.merge_count})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
              No Match ({agg.no_match_count})
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
              Escalate ({agg.escalate_count})
            </span>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-[var(--border-muted)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[var(--border-muted)] bg-white/[0.03]">
                {[
                  "Pair ID",
                  "Decision",
                  "Source",
                  "Zone",
                  "Reviewer",
                  "Decided At",
                  "Override",
                  "Audit Hash",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-3 py-2.5 text-left font-medium text-white/50 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-8 text-center text-white/40"
                  >
                    <RefreshCw className="w-4 h-4 animate-spin inline mr-2" />
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && decisions.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-3 py-8 text-center text-white/30"
                  >
                    No decisions match the selected filters.
                  </td>
                </tr>
              )}
              {!loading &&
                decisions.map((row, i) => (
                  <tr
                    key={row.pair_id}
                    className={`border-b border-[var(--border-subtle)] ${i % 2 === 0 ? "" : "bg-white/[0.015]"} hover:bg-white/[0.04] transition-colors`}
                  >
                    <td className="px-3 py-2 font-mono text-white/60">
                      {row.pair_id.slice(0, 8)}…
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        label={row.decision}
                        variant={decisionVariant(row.decision)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        label={row.decision_source}
                        variant={sourceVariant(row.decision_source)}
                      />
                    </td>
                    <td className="px-3 py-2 text-white/60">
                      {row.zone ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-white/60">
                      {row.reviewer_id ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-white/50 whitespace-nowrap">
                      {new Date(row.decided_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      {row.has_override ? (
                        <Badge label="override" variant="yellow" />
                      ) : (
                        <span className="text-white/30">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <HashChip hash={row.audit_hash} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-xs text-white/50">
          <span>
            Page {currentPage} of {totalPages} · {total.toLocaleString()} total
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => fetch(Math.max(0, offset - PAGE_SIZE))}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--border-muted)] bg-white/[0.04] hover:bg-white/[0.07] disabled:opacity-40 transition"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Prev
            </button>
            <button
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => fetch(offset + PAGE_SIZE)}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[var(--border-muted)] bg-white/[0.04] hover:bg-white/[0.07] disabled:opacity-40 transition"
            >
              Next <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
